import { FC, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Breadcrumb,
  type BreadcrumbItemConfig,
} from "@/components/ui/Breadcrumb";
import { useCompanySegmentContext } from "../hooks/useCompanySegmentContext";
import {
  buildCompanyRouteContextSearch,
  getExploreSearchFromSearch,
} from "@/utils/companyRouteContext";

interface CompanyBreadcrumbsProps {
  companyName?: string;
}

export const CompanyBreadcrumbs: FC<CompanyBreadcrumbsProps> = ({
  companyName,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { segmentId, segmentName, isLoadingSegment } =
    useCompanySegmentContext();
  const preservedSearch = buildCompanyRouteContextSearch({
    search: getExploreSearchFromSearch(location.search),
  });

  const breadcrumbItems = useMemo((): BreadcrumbItemConfig[] => {
    let middleItem: BreadcrumbItemConfig = {
      label: "Companies",
      onClick: () => navigate(`/segments/explore${preservedSearch}`),
    };

    if (segmentId && isLoadingSegment && !segmentName) {
      middleItem = {
        label: "Loading...",
        isLoading: true,
      };
    } else if (segmentId && segmentName) {
      middleItem = {
        label: segmentName,
        onClick: () => navigate(`/segments/${segmentId}${preservedSearch}`),
      };
    }

    const items: BreadcrumbItemConfig[] = [
      {
        label: "Segments",
        onClick: () => navigate("/segments"),
      },
      middleItem,
    ];

    items.push({
      label: companyName ?? "Company",
      isLoading: !companyName,
      isActive: true,
    });

    return items;
  }, [
    companyName,
    isLoadingSegment,
    navigate,
    preservedSearch,
    segmentId,
    segmentName,
  ]);

  return <Breadcrumb items={breadcrumbItems} />;
};
